import * as Sentry from '@sentry/react';

import { parseSSEChunk } from '../../utils/sseParser';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';

export enum IntelligenceEventType {
    PROGRESS = 'PROGRESS',
    COMPLETED = 'COMPLETED',
    LLM_UPDATE = 'LLM_UPDATE',
    SUCCESS_UPDATE = 'SUCCESS_UPDATE',
}

export interface PortSuggestionMessage {
    success: boolean;
    portId: string;
    stopId: string;
    confidenceLevel: number;
    message: string;
    detailedMarkdownMessage: string;
}

export interface OfferSelectionResult {
    success: boolean;
    selectedOfferId: string;
    confidenceLevel: number;
    message: string;
    detailedMarkdownMessage?: string;
}

export interface IntelligenceStreamEvent<TResult = unknown> {
    id: string;
    event: IntelligenceEventType;
    data: string;
    timestamp: number;
    result?: TResult;
    isSuccess?: boolean;
}

export interface PortSuggestionStreamEvent extends IntelligenceStreamEvent<PortSuggestionMessage> {
    event: IntelligenceEventType;
}

export interface OfferSuggestionStreamEvent extends IntelligenceStreamEvent<OfferSelectionResult> {
    event: IntelligenceEventType;
}

const processIntelligenceSSEEvents = <TResult, TEvent extends IntelligenceStreamEvent<TResult>>(
    events: Array<{ id?: string; event?: string; data?: string }>,
    onEvent: (event: TEvent) => void
): void => {
    for (const event of events) {
        if (event.id && event.event && event.data !== undefined) {
            let processedData = event.data;

            if (event.event === IntelligenceEventType.LLM_UPDATE) {
                processedData = event.data.replace(/\\n/g, '\n');
            }

            const streamEvent = {
                id: event.id,
                event: event.event as IntelligenceEventType,
                data: processedData,
                timestamp: Date.now(),
            } as TEvent;

            if (event.event === IntelligenceEventType.SUCCESS_UPDATE) {
                streamEvent.isSuccess = JSON.parse(event.data);
            }

            if (event.event === IntelligenceEventType.COMPLETED) {
                try {
                    streamEvent.result = JSON.parse(event.data) as TResult;
                } catch (parseError) {
                    Sentry.captureException(parseError);
                }
            }

            onEvent(streamEvent);
        }
    }
};

const processSSEEvents = (
    events: Array<{ id?: string; event?: string; data?: string }>,
    onEvent: (event: PortSuggestionStreamEvent) => void
): void => {
    processIntelligenceSSEEvents<PortSuggestionMessage, PortSuggestionStreamEvent>(events, onEvent);
};

const processOfferSSEEvents = (
    events: Array<{ id?: string; event?: string; data?: string }>,
    onEvent: (event: OfferSuggestionStreamEvent) => void
): void => {
    processIntelligenceSSEEvents<OfferSelectionResult, OfferSuggestionStreamEvent>(events, onEvent);
};

const streamIntelligenceData = async <TResult, TEvent extends IntelligenceStreamEvent<TResult>>(
    endpoint: string,
    onEvent: (event: TEvent) => void,
    onError: (error: Error) => void,
    onComplete: () => void,
    processEvents: (
        events: Array<{ id?: string; event?: string; data?: string }>,
        onEvent: (event: TEvent) => void
    ) => void
): Promise<void> => {
    try {
        const response = await authorizedAxiosInstance.get(endpoint, {
            headers: {
                Accept: 'text/event-stream',
            },
            responseType: 'stream',
            adapter: 'fetch',
        });

        const stream = response.data;
        let buffer = '';

        const reader = stream.pipeThrough(new TextDecoderStream()).getReader();

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += value;

            const eventSeparator = '\n\n';
            let separatorIndex = buffer.indexOf(eventSeparator);

            while (separatorIndex !== -1) {
                const eventChunk = buffer.substring(0, separatorIndex + 2);
                buffer = buffer.substring(separatorIndex + 2);

                const events = parseSSEChunk(eventChunk);
                processEvents(events, onEvent);

                separatorIndex = buffer.indexOf(eventSeparator);
            }
        }

        if (buffer.trim()) {
            const events = parseSSEChunk(buffer);
            processEvents(events, onEvent);
        }

        onComplete();
    } catch (error) {
        onError(error as Error);
    }
};

export const streamPortSuggestions = async (
    orderId: string,
    onEvent: (event: PortSuggestionStreamEvent) => void,
    onError: (error: Error) => void,
    onComplete: () => void
): Promise<void> => {
    return streamIntelligenceData<PortSuggestionMessage, PortSuggestionStreamEvent>(
        `/v1/port-suggestions/stream/${orderId}`,
        onEvent,
        onError,
        onComplete,
        processSSEEvents
    );
};

export const streamOfferSuggestions = async (
    orderId: string,
    onEvent: (event: OfferSuggestionStreamEvent) => void,
    onError: (error: Error) => void,
    onComplete: () => void
): Promise<void> => {
    return streamIntelligenceData<OfferSelectionResult, OfferSuggestionStreamEvent>(
        `/v1/offer-suggestions/stream/${orderId}`,
        onEvent,
        onError,
        onComplete,
        processOfferSSEEvents
    );
};
